/**
 * Keeps the app eligible for the browser's back-forward cache (bfcache).
 *
 * A page that registers a legacy `unload` handler is never stored in the
 * bfcache (Chrome reports the blocking reason `UnloadHandlerExistsInMainFrame`),
 * so navigating to an external site and pressing "Back" forces a full reload
 * instead of restoring the page in place. On `/dashboard-bi` the embedded
 * Yellowfin JS API registers such an `unload` handler, which is why returning to
 * a dashboard used to reload the whole app (and come back with the runtime-injected
 * PrimeNG theme missing / broken styles).
 *
 * `unload` is deprecated and unreliable — it doesn't fire on mobile and is
 * intentionally skipped for bfcached pages — so dropping these registrations has
 * no functional downside. The app's own teardown uses `beforeunload`, which does
 * NOT block the bfcache, so nothing of ours is affected.
 *
 * Must run before any third-party script is loaded, i.e. before Angular
 * bootstraps. Patches the prototype so it also covers libraries that grab
 * `EventTarget.prototype.addEventListener` directly.
 */
export function enableBfCache (): void {
	const isMainFrameTarget = ( target: unknown ) =>
		target === window || target === document || target === document.body;

	const proto = EventTarget.prototype;
	const originalAdd = proto.addEventListener;
	proto.addEventListener = function (
		this: EventTarget,
		type: string,
		listener: EventListenerOrEventListenerObject,
		options?: boolean | AddEventListenerOptions,
	) {
		if ( typeof type === 'string' && type.toLowerCase() === 'unload' && isMainFrameTarget( this ) ) {
			return;
		}
		return originalAdd.call( this, type, listener, options );
	};

	// Also neutralise the `window.onunload = ...` / `document.body.onunload = ...`
	// assignment path that some libraries use instead of addEventListener.
	for ( const target of [ window, document.body ].filter( Boolean ) ) {
		try {
			Object.defineProperty( target, 'onunload', {
				configurable: true,
				get: () => null,
				set: () => { /* ignored to preserve bfcache eligibility */ },
			} );
		} catch {
			/* property may be non-configurable in some engines — ignore */
		}
	}
}
